// assetManagerApi — all HTTP calls to the avatar-asset-manager service.
//
// Routed through apiClient.apiFetch so every call carries the avataros_session
// cookie (`credentials: 'include'`) and benefits from the 401→refresh→retry
// interceptor. AAM validates the cookie via its JWT middleware — primary
// auth path for the production SPA.
//
// X-API-Key is a localhost-dev affordance, not a transition: the
// `.avataros.xyz`-scoped cookie can't reach `localhost:NNNN` (RFC 6265
// blocks cross-domain reach). Devs running a local AAM set
// VITE_ASSET_MANAGER_API_KEY in .env.localdev. Production builds leave it
// unset → no header is sent → AAM authenticates via cookie. AAM accepts
// either credential in any env (M2M clients keep working).
//
// VITE_ASSET_MANAGER_URL selects the destination:
//   - "/aam" (default in .env.development) → Vite dev proxy → staging AAM
//   - "http://localhost:8081" (.env.localdev) → local AAM
//   - any absolute URL → direct cross-origin call

import { apiFetch } from './apiClient'
import { cachedFetch } from './aamAssetCache'

const BASE = import.meta.env.VITE_ASSET_MANAGER_URL || 'http://localhost:8081'
const KEY  = import.meta.env.VITE_ASSET_MANAGER_API_KEY || ''

function authHeaders() {
  return KEY ? { 'X-API-Key': KEY } : {}
}

function aam(method, path, body, opts = {}) {
  const headers = { ...authHeaders(), ...opts.headers }
  return apiFetch(`${BASE}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body } : {}),
  })
}

function aamFile(method, path, formData) {
  return apiFetch(`${BASE}${path}`, {
    method,
    headers: authHeaders(),
    body: formData,
  })
}

// ── Identity ──────────────────────────────────────────────────────────────────

// The AAM's view of who this client IS — { id, email, org_id, org_slug, roles,
// is_admin, method, key_id? }, where `id` is the sub the AAM stamps as
// `ownerId` on characters. This is the authoritative identity for anything
// compared against AAM-owned data: under the dev proxy the data calls
// authenticate as the injected API key's owner, which /auth/me knows nothing
// about (see devFakeSession in auth/api.js).
export function getAamMe() {
  return aam('GET', '/api/me')
}

// ── Characters ────────────────────────────────────────────────────────────────

export function listCharacters() {
  return aam('GET', '/api/characters')
}

export function getCharacter(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}`)
}

export function upsertCharacter(config) {
  return aam('POST', '/api/characters', config)
}

export function deleteCharacter(slug) {
  return aam('DELETE', `/api/characters/${encodeURIComponent(slug)}`)
}

// `label`, when a non-empty string, is sent verbatim as the JSON body — the
// server trims it, derives the slug from it, and uniquifies both on
// collision. Omitted/empty → no body, and the server falls back to its own
// "{label} copy" naming (ENG-1044 Task 14).
export const duplicateCharacter = (slug, label) =>
  aam(
    'POST',
    `/api/characters/${encodeURIComponent(slug)}:duplicate`,
    typeof label === 'string' && label.length > 0 ? { label } : undefined,
  )

// ── Personal workspaces (ENG-1028) ────────────────────────────────────────────

// Characters private to someone OTHER than the caller. Org-admin only; a
// non-admin gets their ordinary list back rather than an error, so callers must
// not treat a non-empty result as proof of admin rights.
export function listOthersPrivateCharacters() {
  return aam('GET', '/api/characters?scope=others-private')
}

// Publish a character to the org (shared=true) or pull it back (false).
// Owner-only unless the caller is an org-admin. 409 when the character has no
// owner — a legacy character needs one assigned first.
export function setCharacterSharing(slug, shared) {
  return aam('PUT', `/api/characters/${encodeURIComponent(slug)}/sharing`, { shared })
}

// What un-sharing would take away from the rest of the org. Read this BEFORE
// offering the un-share confirm — the dialog has to state a real blast radius,
// and otherActors is the only honest answer to "is anyone else using this".
export function getShareImpact(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/share-impact`)
}

// Give an ownerless legacy character an owner. Org-admin only (403 otherwise).
// The email is stored as a snapshot because a user id cannot be resolved to a
// name later — the browser cannot reach /users.
export function assignCharacterOwner(slug, userId, email) {
  return aam('PUT', `/api/characters/${encodeURIComponent(slug)}/owner`, { userId, email })
}

export function uploadRig(slug, formData) {
  return aamFile('POST', `/api/characters/${encodeURIComponent(slug)}/rig`, formData)
}

// A character's rig history, newest first. Every upload writes an immutable
// object and appends a row, so this is the list of rigs the character has
// actually had — not a guess derived from storage.
//
// An EMPTY array is a real answer: a character whose rig predates versioning
// has no rows and its current rig still loads. Callers render that as an empty
// state, never as an error (rigVersions.js's rigVersionRows does it).
export function listRigVersions(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/rig-versions`)
}

// Point the character back at an earlier rig. A pointer move server-side: no
// bytes are copied and no version is minted, and the server re-derives the
// material slots from that rig exactly as an upload does.
export function restoreRigVersion(slug, versionId) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/rig-versions/${encodeURIComponent(versionId)}/restore`)
}

// PERMANENT. Destroys every rig version's bytes, the whole history, and the
// character's rig pointer, with no undo and no server-side safety checkpoint.
// Materials and textures survive. Callers must confirm destructively first —
// see isDeleteConfirmed in rigVersions.js.
export function deleteRig(slug) {
  return aam('DELETE', `/api/characters/${encodeURIComponent(slug)}/rig`)
}

// Re-exported for callers that already import from this module (the rig wizards).
// Defined in rigUpload.js so it stays unit-testable without import.meta.env.
export { serverExtractedMaterials } from './rigUpload.js'

// slotKey is the dot-separated slot identifier, e.g. "body.diffuse".
// It is passed as a URL path segment rather than a FormData field.
export function uploadTexture(slug, slotKey, formData) {
  return aamFile('POST', `/api/characters/${encodeURIComponent(slug)}/textures/${encodeURIComponent(slotKey)}`, formData)
}

// ENG-1079: upload a launcher-card thumbnail (a JPEG snapshot of the rendered
// avatar). formData carries one `file` field. The server stores it under a
// fixed key (one per character) and returns { url }; the URL comes back on the
// character's `thumbnailUrl` field thereafter.
export function uploadCharacterThumbnail(slug, formData) {
  return aamFile('POST', `/api/characters/${encodeURIComponent(slug)}/thumbnail`, formData)
}

// ── Animation Assets ──────────────────────────────────────────────────────────

export function listAnimationAssets(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/animation-assets`)
}

export function uploadAnimationAsset(slug, formData) {
  return aamFile('POST', `/api/characters/${encodeURIComponent(slug)}/animation-assets`, formData)
}

// ENG-661: upload a source FBX and convert it server-side to a body GLB. Returns
// a row in conversion_status="converting"; poll listAnimationAssets until ready.
export function convertAnimationAsset(slug, formData) {
  return aamFile('POST', `/api/characters/${encodeURIComponent(slug)}/animation-assets/convert`, formData)
}

export function deleteAnimationAsset(slug, id) {
  return aam('DELETE', `/api/characters/${encodeURIComponent(slug)}/animation-assets/${id}`)
}

// ── Audio Assets ──────────────────────────────────────────────────────────────

export function listAudioAssets(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/audio-assets`)
}

export function uploadAudioAsset(slug, formData) {
  return aamFile('POST', `/api/characters/${encodeURIComponent(slug)}/audio-assets`, formData)
}

export function deleteAudioAsset(slug, id) {
  return aam('DELETE', `/api/characters/${encodeURIComponent(slug)}/audio-assets/${id}`)
}

// ── Gesture Links ─────────────────────────────────────────────────────────────

export function listGestures(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/gestures`)
}

export function createGesture(slug, body) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/gestures`, body)
}

export function updateGesture(id, body, { version } = {}) {
  const opts = version != null ? { headers: { 'If-Match': String(version) } } : {}
  return aam('PATCH', `/api/gestures/${id}`, body, opts)
}

export function deleteGesture(id) {
  return aam('DELETE', `/api/gestures/${id}`)
}

// ── Prefab Links ──────────────────────────────────────────────────────────────

export function listPrefabs(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/prefabs`)
}

export function createPrefab(slug, body) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/prefabs`, body)
}

export function updatePrefab(id, body, { version } = {}) {
  const opts = version != null ? { headers: { 'If-Match': String(version) } } : {}
  return aam('PATCH', `/api/prefabs/${id}`, body, opts)
}

export function deletePrefab(id) {
  return aam('DELETE', `/api/prefabs/${id}`)
}

// ── Blueprints ────────────────────────────────────────────────────────────────

export function getLatestBlueprint(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/blueprint`)
}

// A save REPLACES the stored row rather than merging into it, so a graph sent
// empty deletes the server's copy of it — the ENG-991 incidents (myra
// 2026-07-01, eyeline 2026-08-13). That rule is enforced SERVER-side, by the
// only party holding the stored row to compare against: the AAM refuses a
// graph that is non-empty there and empty here, 400 with a message naming it,
// and `allow_empty: ["body", ...]` in the payload is the deliberate-wipe
// override. apiFetch turns that 400 into an ApiError carrying the message
// verbatim, so callers report the server's words, not a local paraphrase.
//
// This function deliberately does NOT second-guess it. The client-side port
// that used to sit here judged "would this destroy something?" from whether a
// localStorage cache existed, which is a different question, and it locked
// people out of every character whose server body/face graph is legitimately
// empty. See blueprintSaveGuard.js for the full account.
export function saveBlueprint(slug, body) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/blueprint`, body)
}

export function listBlueprintHistory(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/blueprint/history`)
}

export function getBlueprintVersion(slug, versionId) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/blueprint/history/${versionId}`)
}

// ── Blueprint Assets (the blueprint library, ENG-1030) ────────────────────────
//
// One row per GRAPH: `kind` is body | face | events, and each carries that one
// graph. A character has one DEFAULT per kind, and those three are what it
// loads — the singleton endpoints above compose them, so nothing about boot
// changes.

export function listBlueprintAssets(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/blueprint-assets`)
}

// List carries metadata only (name / folder / isDefault / version). Graphs come
// from here, one blueprint at a time — a blueprint's three graph documents are
// the bulk of the row, so the collection response stays small.
export function getBlueprintAsset(id) {
  return aam('GET', `/api/blueprint-assets/${id}`)
}

export function createBlueprintAsset(slug, body) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/blueprint-assets`, body)
}

// GRAPH_KINDS is the closed set the server enforces (migration 00039's CHECK).
// Kept here so a caller cannot invent a fourth kind that the API would reject
// with a 400 the user has to interpret.
export const GRAPH_KINDS = ['body', 'face', 'events']

export function updateBlueprintAsset(id, body, { version } = {}) {
  // A PATCH carrying a graph REPLACES it, exactly like saveBlueprint above, and
  // carries the same server-side guard (assets.go) for the same reason — see
  // the note there. A metadata-only patch (rename, folder move) carries no
  // graph and is nothing for either side to judge.
  const opts = version != null ? { headers: { 'If-Match': String(version) } } : {}
  return aam('PATCH', `/api/blueprint-assets/${id}`, body, opts)
}

export function deleteBlueprintAsset(id) {
  return aam('DELETE', `/api/blueprint-assets/${id}`)
}

// Custom methods are addressed `{id}:verb` (AIP-136) — a colon, not a path
// segment, so a verb can never be confused with a sub-resource id.
export function setDefaultBlueprintAsset(id) {
  return aam('POST', `/api/blueprint-assets/${id}:setDefault`)
}

export function restoreBlueprintAsset(id) {
  return aam('POST', `/api/blueprint-assets/${id}:restore`)
}

// ── Material Assets ───────────────────────────────────────────────────────────

export function listMaterials(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/materials`)
}

export function createMaterial(slug, body) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/materials`, body)
}

export function updateMaterial(id, body, { version } = {}) {
  const opts = version != null ? { headers: { 'If-Match': String(version) } } : {}
  return aam('PATCH', `/api/materials/${id}`, body, opts)
}

export function deleteMaterial(id) {
  return aam('DELETE', `/api/materials/${id}`)
}

// ── Texture Assets ────────────────────────────────────────────────────────────

export function listTextureAssets(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/textures`)
}

export function uploadTextureAsset(slug, formData) {
  return aamFile('POST', `/api/characters/${encodeURIComponent(slug)}/textures`, formData)
}

export function updateTextureAsset(id, body, { version } = {}) {
  const opts = version != null ? { headers: { 'If-Match': String(version) } } : {}
  return aam('PATCH', `/api/textures/${id}`, body, opts)
}

export function deleteTextureAsset(id) {
  return aam('DELETE', `/api/textures/${id}`)
}

// ── Org Environment Assets ─────────────────────────────────────────────────────

export function listOrgEnvironments() {
  return aam('GET', '/api/org/environments')
}

/**
 * One environment, including its `files` map: logical name -> serve URL
 * ("splat", plus one entry per baked artifact that EXISTS). Read a URL from
 * there rather than building one — storage layout is the server's business and
 * has already changed once (ENG-999). The collection above deliberately omits
 * `files`, because it cannot tell whether an artifact is there.
 */
export function getOrgEnvironment(id) {
  return aam('GET', `/api/org/environments/${encodeURIComponent(id)}`)
}

export function uploadOrgEnvironment(formData) {
  return aamFile('POST', '/api/org/environments', formData)
}

// ── OGS bundle (loose per-character inference files in the /api/characters/{slug}/ogs collection) ──
// The single per-character inference-bundle surface: the on-device OGS render path
// (ogsContentPacks.js — geom/appr/mesh/rig) AND the live OGS-streaming path
// (StreamAvatarModel via buildOGSSceneResolver — scene.json/mesh/rig_gather) both
// read their files from this one /ogs collection. One row per file.

export function listOgsAssets(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/ogs`)
}

export function uploadOgsAsset(slug, formData) {
  return aamFile('POST', `/api/characters/${encodeURIComponent(slug)}/ogs`, formData)
}

// Delete one file from a character's /ogs collection by its bare filename.
export function deleteOgsAsset(slug, filename) {
  return aam('DELETE', `/api/characters/${encodeURIComponent(slug)}/ogs/${encodeURIComponent(filename)}`)
}

// Raw byte fetch of an AAM file path (e.g. an /ogs fileUrl) — returns the Response,
// not parsed JSON. Routes to the AAM base with the same auth as the JSON calls.
// This is the network (cache-MISS) path; persistent local caching is layered on
// top by cachedFetchAamFile below.
export function fetchAamFile(path) {
  return fetch(`${BASE}${path}`, { headers: authHeaders(), cache: 'no-store' })
}

// fetchAamFile through the Cache Storage layer (aamAssetCache): a warm load
// serves the /ogs decoders + hair.gsh from local storage with NO network request,
// keyed by path + `size` so a re-upload (changed bytes → changed size) refetches.
// Used by ogsContentPacks for the big inference blobs. `size` is the /ogs list
// row's byte size; omit for a plain cache-first (no size-versioning).
export function cachedFetchAamFile(path, size) {
  return cachedFetch(path, size, { fetchImpl: fetchAamFile, meter: true })
}

export function updateOrgEnvironment(id, body, opts = {}) {
  const headers = opts.version != null ? { 'If-Match': String(opts.version) } : {}
  return aam('PATCH', `/api/org/environments/${id}`, body, { ...opts, headers })
}

export function updateEnvironmentAsset(id, body, { version } = {}) {
  const opts = version != null ? { headers: { 'If-Match': String(version) } } : {}
  return aam('PATCH', `/api/environment-assets/${id}`, body, opts)
}

export function deleteOrgEnvironment(id) {
  return aam('DELETE', `/api/org/environments/${id}`)
}

// ENG-561: trigger an IBL bake (light-probe grid + reflection captures). Returns
// the env asset with bakeStatus="baking"; poll listOrgEnvironments for status.
export function bakeOrgEnvironment(id, params) {
  return aam('POST', `/api/org/environments/${id}/bake`, params ?? {})
}

export function restoreOrgEnvironment(id) {
  return aam('POST', `/api/org/environments/${id}/restore`)
}

// Raw byte fetch of an org environment file (asset ID + filename). Returns
// the Response, not parsed JSON. Routes to the AAM base with the same auth
// as the JSON calls. Used for preview/download of org environment files.
export function fetchOrgEnvironmentFile(assetID, filename) {
  return fetch(`${BASE}/api/org/environment-assets-file/${encodeURIComponent(assetID)}/${encodeURIComponent(filename)}`, {
    headers: authHeaders(),
    cache: 'no-store',
  })
}

// ── Material Mapping ──────────────────────────────────────────────────────────

export function getMaterialMapping(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/material-mapping`)
}

export function setMaterialMapping(slug, mappingArray) {
  return aam('PUT', `/api/characters/${encodeURIComponent(slug)}/material-mapping`, mappingArray)
}

// ── Animation / Audio Metadata Updates ───────────────────────────────────────

export function updateAnimationAsset(id, body, { version } = {}) {
  const opts = version != null ? { headers: { 'If-Match': String(version) } } : {}
  return aam('PATCH', `/api/animation-assets/${id}`, body, opts)
}

export function updateAudioAsset(id, body, { version } = {}) {
  const opts = version != null ? { headers: { 'If-Match': String(version) } } : {}
  return aam('PATCH', `/api/audio-assets/${id}`, body, opts)
}

// ── Custom Gestures (combined upload) ────────────────────────────────────────

export function listCustomGestures(slug) {
  return aam('GET', `/api/custom-gestures/character/${encodeURIComponent(slug)}`)
}

export function uploadCustomGesture(slug, formData) {
  return aamFile('POST', `/api/custom-gestures/character/${encodeURIComponent(slug)}`, formData)
}

export function deleteCustomGesture(slug, id) {
  return aam('DELETE', `/api/custom-gestures/character/${encodeURIComponent(slug)}/${id}`)
}

// ── Custom Prefabs (combined upload) ─────────────────────────────────────────

export function listCustomPrefabs(slug) {
  return aam('GET', `/api/custom-prefabs/character/${encodeURIComponent(slug)}`)
}

export function uploadCustomPrefab(slug, formData) {
  return aamFile('POST', `/api/custom-prefabs/character/${encodeURIComponent(slug)}`, formData)
}

export function updateCustomPrefab(slug, id, body) {
  return aam('PATCH', `/api/custom-prefabs/character/${encodeURIComponent(slug)}/${id}`, body)
}

export function deleteCustomPrefab(slug, id) {
  return aam('DELETE', `/api/custom-prefabs/character/${encodeURIComponent(slug)}/${id}`)
}

// ── Version control (ENG-539) ────────────────────────────────────────────────

export function createCheckpoint(slug, body) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/checkpoints`, body)
}

// branchId optional — when set, scopes the history to a single branch (?branch=).
export function listCheckpoints(slug, branchId) {
  const q = branchId ? `?branch=${encodeURIComponent(branchId)}` : ''
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/checkpoints${q}`)
}

export function getCheckpoint(slug, id) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/checkpoints/${id}`)
}

export function restoreCheckpoint(slug, id) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/checkpoints/${id}/restore`)
}

// Hard reset: discard every checkpoint after `id` on its branch, move the
// branch head back to it, and apply it to live state. Destructive (204).
export function hardResetCheckpoint(slug, id) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/checkpoints/${id}/hard-reset`)
}

export function listBranches(slug) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/branches`)
}

// body: { name, source_branch_id?, source_checkpoint_id? }. A source_checkpoint_id
// forks the new branch from that specific checkpoint.
export function createBranch(slug, body) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/branches`, body)
}

export function closeBranch(slug, id) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/branches/${id}/close`)
}

export function openBranch(slug, id) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/branches/${id}/open`)
}

export function deleteBranch(slug, id) {
  return aam('DELETE', `/api/characters/${encodeURIComponent(slug)}/branches/${id}`)
}

// Working-state changes since a checkpoint (the Changes tab). againstId is the
// branch's latest checkpoint. Returns [{ section, item_id?, type }].
export function getChanges(slug, againstId) {
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/changes?against=${encodeURIComponent(againstId)}`)
}

// Preview a 3-way merge of source→dest branch. Returns { merged, conflicts[] }.
export function previewMerge(slug, sourceBranchId, destBranchId) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/merges/preview`, {
    source_branch_id: sourceBranchId,
    dest_branch_id: destBranchId,
  })
}

// Apply a merge, folding in conflict resolutions: [{ section, item_id?, choice }]
// where choice ∈ 'src'|'dst'. 409 if any conflict is left unresolved.
export function applyMerge(slug, sourceBranchId, destBranchId, resolutions) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/merges/apply`, {
    source_branch_id: sourceBranchId,
    dest_branch_id: destBranchId,
    resolutions: resolutions || [],
  })
}

// Per-asset history: checkpoints where one item changed, newest first. section
// is a manifest section name (material_assets, texture_assets, …); branchId
// optional. Returns [{ checkpoint_id, label, created_by, created_at, branch_id,
// parent_id, action }].
export function getItemHistory(slug, section, itemId, branchId) {
  const q = branchId ? `?branch=${encodeURIComponent(branchId)}` : ''
  return aam('GET', `/api/characters/${encodeURIComponent(slug)}/items/${encodeURIComponent(section)}/${encodeURIComponent(itemId)}/history${q}`)
}

// from/to are checkpoint IDs. Returns [{ section, item_id?, type }].
export function diffCheckpoints(slug, fromCheckpointId, toCheckpointId) {
  return aam('POST', `/api/characters/${encodeURIComponent(slug)}/diff`, {
    from_checkpoint_id: fromCheckpointId,
    to_checkpoint_id: toCheckpointId,
  })
}

// ── Version conflict detection ────────────────────────────────────────────────

// Re-exported from assetErrors.js for convenience; callers import the more
// general assetErrors module for both isRecordGoneError and isVersionConflict.
export { isVersionConflict } from './assetErrors.js'
